<script setup lang="ts">
import { useWindowFocus } from '@vueuse/core'

import { useFetchTransactionDetail } from '~/api/service/transaction/[txnHash]/index.service'
import { useFetchTransactionPendingStatus } from '~/api/service/worker/transaction/pending/status/index.service'
import IconButton from '~/components/button/IconButton.vue'
import TransactionStatusChip from '~/components/chip/TransactionStatusChip.vue'
import CenterEllipsis from '~/components/text/CenterEllipsis.vue'
import { UNIT } from '~/environment-variables/unit.const'

const txnHash = useRoute().params.txnHash as string
const { data, refetch } = useFetchTransactionDetail(txnHash)
const { data: status, refetch: statusRefetch } = useFetchTransactionPendingStatus(txnHash)

const isPending = computed(() => {
  return data.value?.STATTP === 'PENDING'
})

const { copy } = useCopy()

const windowFocused = useWindowFocus()

let intervalId: NodeJS.Timeout

watchEffect(() => {
  if (windowFocused.value && isPending.value) {
    intervalId = setInterval(() => {
      statusRefetch()
    }, 20000)
  } else {
    clearInterval(intervalId)
  }
})

watchEffect(() => {
  if (status.value && status.value.TRXPS !== 'PENDING') {
    refetch()
    clearInterval(intervalId)
  }
})

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<template>
  <div
    v-if="data"
    class="summary-container"
  >
    <div class="flex flex-col gap-y-2">
      <div class="flex items-center">
        <FontAwesomeIcon
          class="mr-1 text-primary/40"
          icon="exchange-alt"
        />
        <span class="text-lg text-primary/40">Txn Hash</span>
      </div>

      <div class="flex items-center gap-x-2">
        <CenterEllipsis
          class="info-txn-hash max-w-full"
          :class="{pending: isPending}"
          :text="txnHash"
        />
        <IconButton
          icon="copy"
          size="lg"
          @click="copy(txnHash)"
        />
      </div>

      <div class="flex items-center">
        <span class="text-base font-bold text-primary/40">{{ data.AG }}</span>
        <TransactionStatusChip
          :status="data.STATTP"
          class="ml-2 flex"
        />
      </div>

      <div class="text-base text-primary/40">
        ({{ data.unixTimeStamp }})
      </div>
    </div>

    <div class="grid grid-cols-1 gap-3 text-lg">
      <div>
        <div class="mb-1 text-base text-primary/40">
          Transfer
        </div>
        <div class="transfer">
          <div class="flex truncate">
            <span class="badge">From</span>
            <CenterEllipsis
              class="address max-w-full"
              :text="data.FROMADDRSINFO.ADDR"
            />
          </div>
          <div class="flex truncate">
            <div class="icon-arrow">
              <FontAwesomeIcon
                class="align-middle"
                icon="arrow-right"
              />
            </div>
            <div class="flex truncate">
              <span class="badge">To</span>
              <CenterEllipsis
                class="address max-w-full"
                :text="data.TOADDRSINFO.ADDR"
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="mb-1 text-base text-primary/40">
          Value
        </div>
        <span class="text-primary">{{ data.value }}<span class="ml-1 text-primary/40">{{ UNIT }}</span></span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.summary-container {
  @apply grid grid-cols-2 gap-x-8 p-4;

  @include sm {
    @apply grid-cols-1 gap-y-4;
  }
}

.info-txn-hash {
  @apply text-3xl font-bold;

  //&:not(.pending) {
  //  @include text-gradient;
  //}

  &.pending {
    @apply text-primary/40;
  }
}

.transfer {
  @apply flex text-primary;

  @include sm {
    @apply flex-col gap-1;
  }

  .badge {
    @apply text-base text-primary/40 py-0.5 px-1 rounded bg-primary/20;
  }

  .address {
    @apply ml-2 text-primary;
  }
}

.icon-arrow {
  @apply mx-4;

  @include sm {
    @apply mx-0 mr-2;
  }
}

</style>
